import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Rating,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import LocalDiningIcon from "@mui/icons-material/LocalDining";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import RateReviewIcon from "@mui/icons-material/RateReview";
import HistoryIcon from "@mui/icons-material/History";
import PaymentsIcon from "@mui/icons-material/Payments";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { motion, AnimatePresence } from "framer-motion";
import { useParams } from "react-router-dom";
import ApiServices from "../../Utils/ApiServices";

const getAccessToken = () => localStorage.getItem("access_token") || "";
const getCustomerId = () =>
  localStorage.getItem("customer_id") ||
  localStorage.getItem("customerId") ||
  localStorage.getItem("logged_customer_id") ||
  localStorage.getItem("user_id") ||
  "";

const getAuthConfig = () => ({
  Authorization: `Bearer ${getAccessToken()}`,
});

const formatDateTime = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString();
};

const STATUS_STYLES = {
  pending:   { bg: "linear-gradient(135deg,#FFB300,#FF8F00)", label: "Pending" },
  accepted:  { bg: "linear-gradient(135deg,#42A5F5,#1E88E5)", label: "Accepted" },
  preparing: { bg: "linear-gradient(135deg,#AB47BC,#7B1FA2)", label: "Preparing" },
  ready:     { bg: "linear-gradient(135deg,#66BB6A,#2E7D32)", label: "Ready" },
  completed: { bg: "linear-gradient(135deg,#26A69A,#00695C)", label: "Completed" },
  cancelled: { bg: "linear-gradient(135deg,#EF5350,#C62828)", label: "Cancelled" },
};
const getStatusStyle = (s) =>
  STATUS_STYLES[s] || { bg: "linear-gradient(135deg,#9E9E9E,#616161)", label: s || "—" };

const BG = "linear-gradient(135deg,#FFF8F0 0%,#FFEFE0 50%,#FFE0CC 100%)";
const REDGRAD = "linear-gradient(135deg,#7A0E0E 0%,#B71C1C 50%,#E64A19 100%)";
const AMBERGRAD = "linear-gradient(135deg,#FF8F00 0%,#FFB300 50%,#FFC107 100%)";
const CARD_GLASS = {
  borderRadius: 4,
  background: "rgba(255,255,255,0.85)",
  backdropFilter: "blur(20px)",
  border: "1px solid rgba(183,28,28,0.08)",
  boxShadow: "0 14px 36px rgba(122,14,14,0.1)",
};

const SectionTitle = ({ icon, children }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" mb={2.5}>
    <Avatar
      sx={{
        width: 36,
        height: 36,
        background: AMBERGRAD,
        boxShadow: "0 6px 16px rgba(255,143,0,0.4)",
      }}
    >
      {icon}
    </Avatar>
    <Typography
      sx={{
        fontFamily: "'Playfair Display',serif",
        fontWeight: 800,
        fontSize: { xs: 18, sm: 22 },
        color: "#2A1810",
      }}
    >
      {children}
    </Typography>
  </Stack>
);

const OrderDetailsPage = () => {
  const { orderId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isLoggedIn = localStorage.getItem("is_logged") === "true";
  const customerId = useMemo(() => getCustomerId(), []);

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const [feedbackRows, setFeedbackRows] = useState([]);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const fetchOrder = async () => {
    if (!isLoggedIn || !customerId || !orderId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await ApiServices.get(
        `/api/v1/customers/${customerId}/orders/${orderId}/`,
        getAuthConfig()
      );
      const orderData = response?.data || null;
      setOrder(orderData);

      const rows =
        orderData?.items?.map((item) => ({
          item_id: item.item_id,
          variant_id: item.variant_id || null,
          item_name: item.item_name,
          variant_name: item.variant_name || "",
          rating: 5,
          comment: "",
        })) || [];

      setFeedbackRows(rows);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.detail || "Failed to load order detail.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const submitFeedback = async () => {
    try {
      setSubmittingFeedback(true);
      const payload = {
        items: feedbackRows.map((row) => ({
          item_id: row.item_id,
          variant_id: row.variant_id,
          rating: Number(row.rating),
          comment: row.comment || "",
        })),
      };

      const response = await ApiServices.post(
        `/api/v1/customers/${customerId}/orders/${orderId}/feedback/`,
        payload,
        getAuthConfig()
      );

      setSnackbar({
        open: true,
        message: response?.data?.message || "Feedback submitted successfully.",
        severity: "success",
      });

      fetchOrder();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.detail || "Failed to submit feedback.",
        severity: "error",
      });
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const sStyle = order ? getStatusStyle(order.status) : null;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: BG,
        position: "relative",
        overflow: "hidden",
        py: { xs: 3, sm: 5, md: 7 },
      }}
    >
      {/* ambient blobs */}
      <Box
        sx={{
          position: "absolute",
          top: -120,
          left: -100,
          width: 380,
          height: 380,
          borderRadius: "50%",
          background: "radial-gradient(circle,rgba(230,74,25,0.25),transparent 70%)",
          filter: "blur(40px)",
          animation: "od-float-1 14s ease-in-out infinite",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -140,
          right: -120,
          width: 420,
          height: 420,
          borderRadius: "50%",
          background: "radial-gradient(circle,rgba(255,179,0,0.22),transparent 70%)",
          filter: "blur(50px)",
          animation: "od-float-2 18s ease-in-out infinite",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1, px: { xs: 2, sm: 3 } }}>
        {!isLoggedIn ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card sx={{ ...CARD_GLASS, p: { xs: 4, sm: 6 }, textAlign: "center" }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  mx: "auto",
                  mb: 2,
                  background: AMBERGRAD,
                  boxShadow: "0 12px 30px rgba(255,143,0,0.4)",
                }}
              >
                <LockOutlinedIcon sx={{ fontSize: 40 }} />
              </Avatar>
              <Typography variant="h5" fontWeight={800} sx={{ color: "#2A1810", mb: 1 }}>
                Login Required
              </Typography>
              <Typography sx={{ color: "#6b4a3a" }}>
                Please login to view your order details.
              </Typography>
            </Card>
          </motion.div>
        ) : loading ? (
          <Box
            sx={{
              minHeight: 360,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress sx={{ color: "#B71C1C" }} size={56} thickness={4} />
            <Typography sx={{ color: "#6b4a3a", fontWeight: 600 }}>
              Plating your order details…
            </Typography>
          </Box>
        ) : !order ? (
          <Card sx={{ ...CARD_GLASS, p: { xs: 4, sm: 6 }, textAlign: "center" }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mx: "auto",
                mb: 2,
                background: "linear-gradient(135deg,#FFE0CC,#FFCDB2)",
                color: "#B71C1C",
              }}
            >
              <SearchOffIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h5" fontWeight={800} sx={{ color: "#2A1810", mb: 1 }}>
              Order not found
            </Typography>
            <Typography sx={{ color: "#6b4a3a" }}>
              We couldn't locate this order in your history.
            </Typography>
          </Card>
        ) : (
          <Stack spacing={{ xs: 2.5, md: 3 }}>
            {/* HERO HEADER */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <Card
                sx={{
                  borderRadius: { xs: 4, md: 6 },
                  background: REDGRAD,
                  color: "#fff",
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: "0 20px 60px rgba(122,14,14,0.35)",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(90deg,transparent,rgba(255,255,255,0.15),transparent)",
                    backgroundSize: "200% 100%",
                    animation: "od-shimmer 6s linear infinite",
                    pointerEvents: "none",
                  }}
                />
                <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 }, position: "relative" }}>
                  <Grid container spacing={{ xs: 2, md: 3 }} alignItems="center">
                    <Grid item xs={12} md={8}>
                      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                        <Avatar
                          sx={{
                            width: { xs: 52, sm: 64 },
                            height: { xs: 52, sm: 64 },
                            background: AMBERGRAD,
                            boxShadow: "0 10px 24px rgba(255,143,0,0.5)",
                          }}
                        >
                          <ReceiptLongIcon sx={{ fontSize: { xs: 26, sm: 32 } }} />
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontFamily: "'Playfair Display',serif",
                              fontWeight: 800,
                              fontSize: { xs: 24, sm: 32, md: 40 },
                              lineHeight: 1.1,
                              wordBreak: "break-word",
                            }}
                          >
                            {order.order_number}
                          </Typography>
                          <Typography sx={{ opacity: 0.85, fontSize: { xs: 12, sm: 14 }, mt: 0.5 }}>
                            Order details & timeline
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack spacing={0.7} sx={{ opacity: 0.95, fontSize: { xs: 13, sm: 14 } }}>
                        <Typography sx={{ fontSize: "inherit" }}>
                          <strong>Placed:</strong> {formatDateTime(order.placed_at)}
                        </Typography>
                        <Typography sx={{ fontSize: "inherit" }}>
                          <strong>Updated:</strong> {formatDateTime(order.updated_at)}
                        </Typography>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Stack
                        spacing={1.2}
                        alignItems={{ xs: "flex-start", md: "flex-end" }}
                      >
                        <Chip
                          label={sStyle.label}
                          sx={{
                            background: sStyle.bg,
                            color: "#fff",
                            fontWeight: 800,
                            textTransform: "capitalize",
                            px: 1,
                            boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
                          }}
                        />
                        <Box
                          sx={{
                            px: 2.5,
                            py: 1.2,
                            borderRadius: 999,
                            bgcolor: "#fff",
                            color: "#7A0E0E",
                            fontWeight: 800,
                            fontSize: { xs: 18, sm: 22 },
                            fontFamily: "'Playfair Display',serif",
                            boxShadow: "0 8px 22px rgba(0,0,0,0.2)",
                          }}
                        >
                          ${Number(order.total || 0).toFixed(2)}
                        </Box>
                        <Chip
                          icon={<LocalDiningIcon sx={{ color: "#fff !important" }} />}
                          label={order.service_type}
                          sx={{
                            bgcolor: "rgba(255,255,255,0.2)",
                            color: "#fff",
                            fontWeight: 700,
                            textTransform: "capitalize",
                            backdropFilter: "blur(10px)",
                            border: "1px solid rgba(255,255,255,0.3)",
                          }}
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </motion.div>

            {/* MAIN GRID */}
            <Grid container spacing={{ xs: 2.5, md: 3 }}>
              {/* ITEMS */}
              <Grid item xs={12} md={7}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                >
                  <Card sx={CARD_GLASS}>
                    <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                      <SectionTitle icon={<RestaurantMenuIcon />}>Ordered Items</SectionTitle>

                      <Stack spacing={2}>
                        {(order.items || []).map((item, idx) => (
                          <motion.div
                            key={item.id}
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.15 + idx * 0.05 }}
                          >
                            <Card
                              sx={{
                                borderRadius: 3,
                                border: "1px solid rgba(183,28,28,0.1)",
                                background: "rgba(255,248,240,0.7)",
                                transition: "all 0.25s",
                                "&:hover": {
                                  borderColor: "rgba(183,28,28,0.3)",
                                  transform: "translateX(4px)",
                                  boxShadow: "0 8px 22px rgba(122,14,14,0.12)",
                                },
                              }}
                            >
                              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="flex-start"
                                  spacing={1.5}
                                >
                                  <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
                                    <Avatar
                                      sx={{
                                        width: 44,
                                        height: 44,
                                        background: AMBERGRAD,
                                        flexShrink: 0,
                                        fontWeight: 800,
                                        fontSize: 14,
                                        boxShadow: "0 4px 12px rgba(255,143,0,0.3)",
                                      }}
                                    >
                                      ×{item.quantity}
                                    </Avatar>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                      <Typography
                                        fontWeight={800}
                                        sx={{ color: "#2A1810", fontSize: { xs: 14, sm: 15 }, wordBreak: "break-word" }}
                                      >
                                        {item.item_name}
                                      </Typography>
                                      {item.variant_name ? (
                                        <Typography variant="body2" sx={{ color: "#8b6a5a", mt: 0.3 }}>
                                          {item.variant_name}
                                        </Typography>
                                      ) : null}
                                      {item.notes ? (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            display: "inline-block",
                                            mt: 0.8,
                                            px: 1,
                                            py: 0.3,
                                            borderRadius: 1,
                                            bgcolor: "rgba(255,179,0,0.15)",
                                            color: "#7A4A00",
                                            fontWeight: 600,
                                          }}
                                        >
                                          📝 {item.notes}
                                        </Typography>
                                      ) : null}
                                    </Box>
                                  </Stack>

                                  <Typography
                                    sx={{
                                      fontFamily: "'Playfair Display',serif",
                                      fontWeight: 800,
                                      fontSize: { xs: 16, sm: 18 },
                                      color: "#7A0E0E",
                                      flexShrink: 0,
                                    }}
                                  >
                                    ${Number(item.line_total || 0).toFixed(2)}
                                  </Typography>
                                </Stack>
                              </CardContent>
                            </Card>
                          </motion.div>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>

              {/* RIGHT COLUMN */}
              <Grid item xs={12} md={5}>
                <Stack spacing={{ xs: 2.5, md: 3 }}>
                  {/* BILLING */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.15 }}
                  >
                    <Card sx={CARD_GLASS}>
                      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                        <SectionTitle icon={<PaymentsIcon />}>Billing Summary</SectionTitle>

                        <Stack spacing={1.4}>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography sx={{ color: "#6b4a3a" }}>Subtotal</Typography>
                            <Typography fontWeight={700} sx={{ color: "#2A1810" }}>
                              ${Number(order.subtotal || 0).toFixed(2)}
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography sx={{ color: "#6b4a3a" }}>Discount</Typography>
                            <Typography fontWeight={700} sx={{ color: "#2E7D32" }}>
                              − ${Number(order.discount || 0).toFixed(2)}
                            </Typography>
                          </Stack>
                          <Divider sx={{ borderColor: "rgba(183,28,28,0.15)", borderStyle: "dashed" }} />
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography
                              sx={{
                                fontFamily: "'Playfair Display',serif",
                                fontWeight: 800,
                                fontSize: { xs: 18, sm: 20 },
                                color: "#2A1810",
                              }}
                            >
                              Total
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "'Playfair Display',serif",
                                fontWeight: 800,
                                fontSize: { xs: 22, sm: 26 },
                                background: REDGRAD,
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                              }}
                            >
                              ${Number(order.total || 0).toFixed(2)}
                            </Typography>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* TIMELINE */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.25 }}
                  >
                    <Card sx={CARD_GLASS}>
                      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                        <SectionTitle icon={<HistoryIcon />}>Order Timeline</SectionTitle>

                        <Stack spacing={0} sx={{ position: "relative" }}>
                          {(order.status_logs || []).map((log, idx) => {
                            const last = idx === order.status_logs.length - 1;
                            const logStyle = getStatusStyle(log.status);
                            return (
                              <Box key={`${log.status}-${idx}`} sx={{ position: "relative", pl: 4, pb: last ? 0 : 2.5 }}>
                                {/* dot */}
                                <Box
                                  sx={{
                                    position: "absolute",
                                    left: 0,
                                    top: 4,
                                    width: 16,
                                    height: 16,
                                    borderRadius: "50%",
                                    background: logStyle.bg,
                                    boxShadow: "0 0 0 4px rgba(255,255,255,0.9), 0 4px 10px rgba(0,0,0,0.15)",
                                    animation: idx === 0 ? "od-pulse-dot 2s ease-in-out infinite" : "none",
                                  }}
                                />
                                {/* line */}
                                {!last && (
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      left: 7.5,
                                      top: 22,
                                      bottom: 0,
                                      width: 2,
                                      background: "linear-gradient(180deg,rgba(183,28,28,0.3),rgba(183,28,28,0.1))",
                                    }}
                                  />
                                )}
                                <Typography
                                  fontWeight={800}
                                  sx={{ textTransform: "capitalize", color: "#2A1810", fontSize: { xs: 14, sm: 15 } }}
                                >
                                  {log.status}
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#6b4a3a", mt: 0.3 }}>
                                  {log.note || "—"}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "#8b6a5a", display: "block", mt: 0.3 }}>
                                  {formatDateTime(log.changed_at)}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Stack>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Stack>
              </Grid>
            </Grid>

            {/* FEEDBACK */}
            <AnimatePresence>
              {order.status === "completed" && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <Card sx={CARD_GLASS}>
                    <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
                      <SectionTitle icon={<RateReviewIcon />}>Submit Feedback</SectionTitle>
                      <Typography sx={{ color: "#6b4a3a", mb: 3, mt: -1 }}>
                        How was your meal? Your taste matters to us.
                      </Typography>

                      <Stack spacing={2.5}>
                        {feedbackRows.map((row, index) => (
                          <motion.div
                            key={`${row.item_id}-${index}`}
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: index * 0.05 }}
                          >
                            <Card
                              sx={{
                                borderRadius: 3,
                                border: "1px solid rgba(183,28,28,0.1)",
                                background: "rgba(255,248,240,0.7)",
                              }}
                            >
                              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                                <Stack spacing={2}>
                                  <Box>
                                    <Typography fontWeight={800} sx={{ color: "#2A1810" }}>
                                      {row.item_name}
                                    </Typography>
                                    {row.variant_name ? (
                                      <Typography variant="body2" sx={{ color: "#8b6a5a", mt: 0.3 }}>
                                        {row.variant_name}
                                      </Typography>
                                    ) : null}
                                  </Box>

                                  <Box>
                                    <Typography variant="body2" sx={{ color: "#6b4a3a", mb: 1, fontWeight: 600 }}>
                                      Your Rating
                                    </Typography>
                                    <Rating
                                      size={isMobile ? "medium" : "large"}
                                      value={Number(row.rating)}
                                      onChange={(event, value) => {
                                        setFeedbackRows((prev) =>
                                          prev.map((item, idx) =>
                                            idx === index
                                              ? { ...item, rating: value || 1 }
                                              : item
                                          )
                                        );
                                      }}
                                      sx={{
                                        "& .MuiRating-iconFilled": { color: "#FFB300" },
                                        "& .MuiRating-iconHover": { color: "#FF8F00" },
                                      }}
                                    />
                                  </Box>

                                  <TextField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    placeholder="Tell us what you loved (or didn't)…"
                                    value={row.comment}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setFeedbackRows((prev) =>
                                        prev.map((item, idx) =>
                                          idx === index
                                            ? { ...item, comment: value }
                                            : item
                                        )
                                      );
                                    }}
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                        background: "#fff",
                                        "&:hover fieldset": { borderColor: "#B71C1C" },
                                        "&.Mui-focused fieldset": { borderColor: "#B71C1C" },
                                      },
                                    }}
                                  />
                                </Stack>
                              </CardContent>
                            </Card>
                          </motion.div>
                        ))}

                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                          <Button
                            fullWidth={isMobile}
                            startIcon={submittingFeedback ? null : <CheckCircleIcon />}
                            onClick={submitFeedback}
                            disabled={submittingFeedback}
                            sx={{
                              alignSelf: "flex-start",
                              px: 4,
                              py: 1.4,
                              borderRadius: 999,
                              background: REDGRAD,
                              color: "#fff",
                              fontWeight: 700,
                              fontSize: 15,
                              boxShadow: "0 12px 30px rgba(183,28,28,0.35)",
                              "&:hover": { boxShadow: "0 18px 40px rgba(183,28,28,0.5)" },
                              "&.Mui-disabled": { background: "#ccc", color: "#fff" },
                            }}
                          >
                            {submittingFeedback ? (
                              <Stack direction="row" spacing={1.5} alignItems="center">
                                <CircularProgress size={18} sx={{ color: "#fff" }} />
                                <span>Submitting…</span>
                              </Stack>
                            ) : (
                              "Submit Feedback"
                            )}
                          </Button>
                        </motion.div>
                      </Stack>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>
          </Stack>
        )}
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ borderRadius: 3, fontWeight: 600, boxShadow: "0 12px 30px rgba(0,0,0,0.2)" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrderDetailsPage;
